import { useState, useContext, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import type { Table as ArrowTable } from "apache-arrow";

import { ParsedParquetData } from "../../utils/parquet";
import { PivotViewConfig } from "../../entities/pivot_view_config";
import { ChartConfigProp } from "../../entities/chart_config";
import { TransformedCustomViewControlsContainer } from "./controls";
import { TransformedCustomViewViewerContainer } from "./viewer";
import { useParams } from "../../hooks/use_date_pivot_params";
import { useViewTitle } from "../../hooks/use_view_title";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { ChatContext } from "../../contexts/chat/context";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { useCustomer } from "../../contexts/app/provider";
import { createCommonViewContext } from "../../entities/common_view_context";

export type BackendExportFn = (excludeDetailRows: boolean) => Promise<void>;

export const TransformedCustomViewContainer = <
  T extends DatePivotParams = DatePivotParams,
>({
  viewControllerConfig,
  defaultConfig,
  viewerComponent,
  chartConfig,
  customChartViewerComponent,
}: {
  viewControllerConfig: PivotViewConfig<T>;
  defaultConfig: T;
  viewerComponent?: React.ComponentType<any>;
  chartConfig?: ChartConfigProp;
  customChartViewerComponent?: React.ComponentType<any>;
}) => {
  const {
    datasetName,
    transformId,
    tableType,
    columnHeaderMap,
    columnFormatters,
  } = viewControllerConfig;

  const { mainRun, comparedRun, lastSuccessfulRun } =
    useContext(VersionedDataContext);
  const { registerCurrentViewContext } = useContext(ChatContext);
  const { selectedCustomerId } = useCustomer();
  const { loading, viewParams, setViewParams } = useParams(
    defaultConfig,
    datasetName,
    transformId,
  );

  useEffect(() => {
    if (
      !registerCurrentViewContext ||
      !viewControllerConfig.viewContextSurfaceId ||
      loading ||
      !viewParams
    ) {
      return;
    }
    const viewContext = createCommonViewContext({
      surfaceId: viewControllerConfig.viewContextSurfaceId,
      measure: viewParams.measure,
      dateHierarchy: viewParams.dateHierarchy,
      minDate: viewParams.minDate,
      maxDate: viewParams.maxDate,
      segments: viewParams.segments,
      filters: viewParams.filters,
      postFilters: viewParams.postFilters,
    });
    return viewContext ? registerCurrentViewContext(viewContext) : undefined;
  }, [
    registerCurrentViewContext,
    viewControllerConfig.viewContextSurfaceId,
    loading,
    viewParams,
  ]);

  const [controllerLoading, setControllerLoading] = useState(false);
  const [isChartView, setIsChartView] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedParquetData | null>(null);
  const [columnSchema, setColumnSchema] = useState<
    Record<
      string,
      {
        dtype: string;
        min?: string;
        max?: string;
        unique_values?: string[];
        value_counts?: Record<string, number>;
      }
    >
  >({});

  // Modify segments only when switching to chart view
  useEffect(() => {
    if (isChartView && viewParams?.segments && viewParams.segments.length > 1) {
      // Only keep the first segment when switching to chart view
      setViewParams({
        ...viewParams,
        segments: [viewParams.segments[0]],
      } as T);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChartView]);

  // Initialize sortConfig as empty array - let getMatrix handle initial sort
  const [sortConfig, setSortConfig] = useState<
    Array<{ key: string; direction: "asc" | "desc"; index: number }>
  >([]);

  // Add expand/collapse state for inventory health
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // In-memory ArrowTable for base64 data that is wrapped as chunked.
  // The viewer sets this when a small dataset arrives as base64; the controls
  // use it for the frontend CSV export path.
  const [inMemoryArrowTable, setInMemoryArrowTable] =
    useState<ArrowTable | null>(null);

  // Backend CSV export callback (set by the viewer when chunked path is active)
  const [backendExportFn, setBackendExportFn] =
    useState<BackendExportFn | null>(null);

  // Stable wrapper so the controls component gets a consistent reference
  const handleBackendExport = useCallback(
    async (excludeDetailRows: boolean) => {
      if (backendExportFn) await backendExportFn(excludeDetailRows);
    },
    [backendExportFn],
  );

  // Reset sortConfig when measure changes (for Inventory Health views with measure-based default sorting)
  useEffect(() => {
    if (
      viewControllerConfig.title.startsWith("Inventory Health") &&
      viewParams?.measure
    ) {
      setSortConfig([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewParams?.measure]);

  // Surface this view's title in the global TopHeader. Subscribes only to the
  // stable setter context, so title changes never re-render this subtree.
  useViewTitle(viewControllerConfig.title);

  const scopeRunToSelectedCustomer = (run: typeof mainRun) =>
    run &&
    (!selectedCustomerId ||
      selectedCustomerId === run.runSettings.organizationId)
      ? run
      : null;
  const scopedMainRun = scopeRunToSelectedCustomer(mainRun);
  const scopedComparedRun = scopeRunToSelectedCustomer(comparedRun);
  const scopedLastSuccessfulRun = scopeRunToSelectedCustomer(lastSuccessfulRun);

  const lockedSetId = scopedMainRun?.runSettings
    ? `${scopedMainRun.runSettings?.inputId}|${scopedMainRun.runSettings?.settingsNameAndVersion}`
    : null;
  const comparedToSetId = scopedComparedRun?.runSettings
    ? `${scopedComparedRun.runSettings?.inputId}|${scopedComparedRun.runSettings?.settingsNameAndVersion}`
    : null;

  // Existing views intentionally send null when no explicit main run is
  // selected so Perspective follows the server's latest successful run. Cell
  // accepted-view consumers need a stronger contract: request the exact run
  // used by the visible view and bind actions only after that response is
  // accepted. A source observer can publish current-view data without enabling
  // generic cell affordances (for example, PO Inbox).
  const requiresAcceptedViewBinding = Boolean(
    viewControllerConfig.cellExplanation ||
      viewControllerConfig.onForecastCellExplain ||
      viewControllerConfig.onRenderedSourceChange,
  );
  const visibleRun = scopedMainRun ?? scopedLastSuccessfulRun;
  const renderedRun = requiresAcceptedViewBinding ? visibleRun : null;
  const requestedRunSettings = requiresAcceptedViewBinding
    ? (renderedRun?.runSettings ?? null)
    : (scopedMainRun?.runSettings ?? null);

  if (loading || !viewParams) {
    return null;
  }

  // Type-safe wrapper for setViewParams that handles the generic type T
  const handleSetViewParams = (params: T | ((prev: T | null) => T | null)) => {
    setViewParams(params as any);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <TransformedCustomViewControlsContainer<T>
        viewControllerConfig={viewControllerConfig}
        controllerLoading={controllerLoading}
        isChartView={isChartView}
        setIsChartView={setIsChartView}
        params={viewParams as T}
        setParams={handleSetViewParams}
        setControllerLoading={setControllerLoading}
        lockedSetId={lockedSetId}
        comparedToSetId={comparedToSetId}
        parsedData={parsedData}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        columnSchema={columnSchema}
        setColumnSchema={setColumnSchema}
        onBackendExport={backendExportFn ? handleBackendExport : undefined}
        inMemoryArrowTable={inMemoryArrowTable}
      >
        <TransformedCustomViewViewerContainer<T>
          viewControllerConfig={viewControllerConfig}
          params={viewParams as T}
          runSettings={requestedRunSettings}
          renderedRun={renderedRun}
          comparedToRunSettings={scopedComparedRun?.runSettings || null}
          viewerComponent={viewerComponent as React.ComponentType<any>}
          tableType={tableType || "pivot"}
          columnHeaderMap={columnHeaderMap}
          columnFormatters={columnFormatters}
          parsedData={parsedData}
          setParsedData={setParsedData}
          isChartView={isChartView}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
          chartConfig={chartConfig as ChartConfigProp}
          ChartComponent={
            customChartViewerComponent as React.ComponentType<any>
          }
          columnSchema={columnSchema}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          setBackendExportFn={setBackendExportFn}
          setInMemoryArrowTable={setInMemoryArrowTable}
        />
      </TransformedCustomViewControlsContainer>
    </Box>
  );
};
